import React, { useEffect, useRef, useState } from "react";
import "../../pages/signin/sigin.css";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { auth } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import {
  fetchProfileDashboard,
  hasRequiredAddressInfo,
  hasRequiredPersonalInfo,
} from "../../api/profile.api";

const PROFILE_COMPLETION_PROMPT_KEY = "show_profile_completion_prompt";

const SignInForm = ({ onClose, onSwitchToSignUp, onForgot }) => {
  const [mode, setMode] = useState("email");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [phoneStep, setPhoneStep] = useState("phone");
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const recaptchaRef = useRef(null);

  const { signIn, signInWithPhone } = useAuth();

  useEffect(() => {
    return () => {
      if (recaptchaRef.current) {
        recaptchaRef.current.clear();
        recaptchaRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = setTimeout(() => {
      setResendCooldown((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const resetPhoneState = () => {
    setPhoneStep("phone");
    setOtpCode("");
    setConfirmationResult(null);
    setResendCooldown(0);
    setInfoMessage("");
    if (recaptchaRef.current) {
      recaptchaRef.current.clear();
      recaptchaRef.current = null;
    }
  };

  const normalizePhoneNumber = (value) => {
    const trimmed = value.trim();
    if (!trimmed) return "";

    if (trimmed.startsWith("+")) {
      return trimmed;
    }

    const digits = trimmed.replace(/\D/g, "");
    if (digits.length === 10) {
      return `+91${digits}`;
    }

    if (digits.length > 10) {
      return `+${digits}`;
    }

    return "";
  };

  const getRecaptchaVerifier = () => {
    if (recaptchaRef.current) {
      recaptchaRef.current.clear();
    }

    recaptchaRef.current = new RecaptchaVerifier(auth, "recaptcha-container", {
      size: "invisible",
    });

    return recaptchaRef.current;
  };

  const getFirebaseAuthErrorMessage = (err, phase = "send") => {
    const code = err?.code || "";

    if (code === "auth/configuration-not-found") {
      return "Phone OTP is not enabled in Firebase. Enable Phone provider in Firebase Console > Authentication > Sign-in method.";
    }

    if (code === "auth/operation-not-allowed") {
      return "Phone login is disabled for this Firebase project. Enable Phone provider in Firebase Console > Authentication > Sign-in method.";
    }

    if (code === "auth/unauthorized-domain") {
      return "This domain is not authorized for Firebase Auth. Add localhost and 127.0.0.1 in Firebase Console > Authentication > Settings > Authorized domains.";
    }

    if (code === "auth/invalid-phone-number") {
      return "Invalid phone number format. Use format like +919876543210.";
    }

    if (code === "auth/too-many-requests") {
      return "Too many OTP attempts. Please wait a few minutes and try again.";
    }

    if (code === "auth/captcha-check-failed" || code === "auth/missing-app-credential") {
      return "reCAPTCHA verification failed. Reload the page and try again.";
    }

    if (phase === "verify") {
      if (code === "auth/invalid-verification-code") {
        return "Invalid OTP code. Please check and try again.";
      }
      if (code === "auth/code-expired") {
        return "OTP expired. Please request a new OTP.";
      }
    }

    return err?.message || "Something went wrong with phone authentication. Please try again.";
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;

    setIsLoading(true);
    setError("");
    setInfoMessage("");

    try {
      const result = await signIn(email, password, rememberMe);
      if (result.success) {
        onClose();
        window.location.reload();
      } else {
        setError(result.message || "Invalid credentials");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (isLoading) return;
    if (phoneStep === "otp" && resendCooldown > 0) return;

    const formattedPhone = normalizePhoneNumber(phoneNumber);
    if (!formattedPhone) {
      setError("Enter a valid phone number (example: +919876543210).");
      return;
    }

    setIsLoading(true);
    setError("");
    setInfoMessage("");

    try {
      const verifier = getRecaptchaVerifier();
      const result = await signInWithPhoneNumber(auth, formattedPhone, verifier);
      setConfirmationResult(result);
      setPhoneStep("otp");
      setResendCooldown(30);
      setInfoMessage("OTP sent successfully. Please enter the OTP to continue.");
    } catch (err) {
      console.error("Phone OTP send failed:", err);
      setError(getFirebaseAuthErrorMessage(err, "send"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (isLoading) return;

    if (!confirmationResult) {
      setError("Please request OTP first.");
      return;
    }

    if (!otpCode.trim()) {
      setError("Please enter OTP.");
      return;
    }

    setIsLoading(true);
    setError("");
    setInfoMessage("");

    try {
      const credential = await confirmationResult.confirm(otpCode.trim());
      const idToken = await credential.user.getIdToken();
      const result = await signInWithPhone(idToken);

      if (result.success) {
        const profile = await fetchProfileDashboard().catch(() => null);
        const missingPersonal = !hasRequiredPersonalInfo(profile?.personal_info);
        const missingAddress = !hasRequiredAddressInfo(profile?.default_address);

        if (missingPersonal || missingAddress) {
          window.sessionStorage.setItem(PROFILE_COMPLETION_PROMPT_KEY, "1");
          onClose();
          window.location.reload();
          return;
        }

        onClose();
        window.location.reload();
      } else {
        setError(result.message || "Phone authentication failed.");
      }
    } catch (err) {
      console.error("Phone OTP verify failed:", err);
      setError(getFirebaseAuthErrorMessage(err, "verify"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleMobileModeClick = () => {
    setError("");

    if (mode === "email") {
      setInfoMessage(
        "Sign in with mobile is currently not available. Please use email and password for now."
      );
      return;
    }

    setInfoMessage("");
    resetPhoneState();
    setMode("email");
  };

  return (
    <div className="cart-overlay signin-overlay" onClick={onClose}>
      <div className="cart-popup signin-popup" onClick={(e) => e.stopPropagation()}>
        <div className="cart-header">
          <h3>{mode === "email" ? "SIGN IN" : "PHONE AUTH"}</h3>
          <span className="close-btn" onClick={onClose}>×</span>
        </div>

        <div style={{ padding: "20px" }}>
          {error && (
            <div className="info-box" style={{ background: "#fee", color: "#c33", marginBottom: 16 }}>
              {error}
            </div>
          )}

          {infoMessage && (
            <div className="info-box" style={{ marginBottom: 16 }}>
              {infoMessage}
            </div>
          )}

          {mode === "email" && (
            <form onSubmit={handleEmailSubmit}>
              <div className="signin-input-group">
                <input
                  type="email"
                  className="signin-input"
                  placeholder="Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="signin-input-group">
                <input
                  type="password"
                  className="signin-input"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <div className="signin-options">
                <label className="remember-me">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span>Remember me</span>
                </label>

                <a
                  href="#forgot"
                  className="forgot-password"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onForgot();
                  }}
                >
                  Forgot Password?
                </a>
              </div>

              <button type="submit" className="signin-btn" disabled={isLoading}>
                {isLoading ? "SIGNING IN..." : "SIGN IN"}
              </button>
            </form>
          )}

          {mode === "phone" && (
            <div className="mobile-otp-section">
              {phoneStep === "phone" && (
                <>
                  <div className="signin-input-group">
                    <input
                      type="tel"
                      className="signin-input"
                      placeholder="Phone Number (+91XXXXXXXXXX)"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                    />
                  </div>

                  <button
                    type="button"
                    className="signin-btn"
                    onClick={handleSendOtp}
                    disabled={isLoading}
                  >
                    {isLoading ? "SENDING OTP..." : "NEXT"}
                  </button>
                </>
              )}

              {phoneStep === "otp" && (
                <>
                  <div className="signin-input-group">
                    <input
                      type="text"
                      className="signin-input"
                      placeholder="Enter OTP"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value)}
                    />
                  </div>

                  <button
                    type="button"
                    className="signin-btn"
                    onClick={handleVerifyOtp}
                    disabled={isLoading}
                  >
                    {isLoading ? "VERIFYING..." : "VERIFY OTP"}
                  </button>

                  <button
                    type="button"
                    className="mobile-btn"
                    onClick={handleSendOtp}
                    disabled={isLoading || resendCooldown > 0}
                  >
                    {resendCooldown > 0
                      ? `Resend OTP in 00:${String(resendCooldown).padStart(2, "0")}`
                      : "Resend OTP"}
                  </button>

                  <button
                    type="button"
                    className="mobile-btn"
                    onClick={resetPhoneState}
                    disabled={isLoading}
                  >
                    Change Number
                  </button>
                </>
              )}

              <div id="recaptcha-container" />
            </div>
          )}

          <button
            type="button"
            className="mobile-btn"
            onClick={handleMobileModeClick}
            disabled={isLoading}
          >
            {mode === "email" ? "Sign In With Mobile OTP" : "Use Email/Password Instead"}
          </button>
        </div>

        <div className="signup-link">
          <span>New Member? </span>
          <a
            href="#signup"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSwitchToSignUp();
            }}
          >
            Sign Up
          </a>
        </div>
      </div>
    </div>
  );
};

export default SignInForm;
